import {Directive, EventEmitter, Inject, Input, OnChanges, OnInit, Output, SimpleChanges} from '@angular/core';
import {FixedShape} from '@domain/drawing-library/fixed-shape.enum';
import {Drawing} from '@domain/production-schedule/drawing';
import {DrawingConfiguration} from '@domain/production-schedule/drawing-configuration';
import {DrawingConfigurationWithColorAndYarnSet} from '@domain/production-schedule/drawing-configuration-with-color-and-yarn-set';
import {ProductionSchedule} from '@domain/production-schedule/production-schedule';
import {ProductionScheduleDrawingViewMode} from '@domain/production-schedule/production-schedule-drawing-view-mode.enum';
import {ProductionScheduleItemInPathGroup} from '@domain/production-schedule/production-schedule-item-in-path-group';
import {ProductionSchedulePath} from '@domain/production-schedule/production-schedule-path';
import {ProductionSchedulePathsOfColoredYarnSet} from '@domain/production-schedule/production-schedule-paths-of-colored-yarn-set';
import {Permission} from '@domain/profile/permission.enum';
import {Subscription} from '@domain/profile/subscription';
import {TargetForListOfDrawingsEnum} from '@domain/target-for-list-of-drawings.enum';
import {OverviewListColoredYarnSetWithStartDent} from '@domain/textile-data/creel/overview-list-colored-yarn-set-with-start-dent';
import {StatusBarTarget} from '@domain/textile-data/status-bar-target.constants';
import {AUTHENTICATION, Authentication} from '@infrastructure/http/authentication/authentication';
import {Drawings, DRAWINGS} from '@infrastructure/http/drawing/drawings';
import {HttpStatusBarService} from '@infrastructure/http/status-bar/http-status-bar.service';
import {OrderlineSettingsData} from '@presentation/components/dialog-button/dialog-data/order-lines-settings/order-lines-setting-data.interface';
import {ListDrawingConfiguration} from '@presentation/components/drawing-list/list-drawing-configuration';
import {DrawingLibraryHelper} from '@presentation/pages/texedit/drawing-library/drawing-library-new-listview/drawing-library-helper';
import {BackendLimitsConstants} from '@shared/constants/backend-limits.constants';
import {
  AssertionUtils,
  BaseComponent,
  ColDefBuilderFactoryService,
  convertToCommercialUnitCentimeter,
  convertUnit,
  DialogBuilderFactoryService,
  EmptyCellRendererComponent,
  GridModel,
  GridOptionsBuilderFactoryService,
  NavigationHelperPrototypeService,
  StatusBar,
  TranslateService,
  Unit,
  UnitValue
} from '@vdw/angular-component-library';
import {AgGridAngular} from 'ag-grid-angular';
import {
  AdvancedFilterModel,
  CellMouseOverEvent,
  CellRendererSelectorResult,
  ColDef,
  FilterModel,
  ICellRendererParams,
  IServerSideDatasource,
  IServerSideGetRowsParams,
  RowNode,
  RowSelectedEvent,
  SortModelItem,
  ValueFormatterParams,
  ValueGetterParams
} from 'ag-grid-community';
import {Observable, of, Subject} from 'rxjs';
import {map, takeUntil} from 'rxjs/operators';
import {PRODUCTION_SCHEDULE_PLAN, ProductionSchedulePlan} from '../../../plan/production-schedule-plan';
import {GroupedDrawing} from '../drawings-grouped-view/grouped-drawing';
import {GroupedDrawingsSelectionService} from '../drawings-grouped-view/grouped-drawings-selection.service';

@Directive()
export abstract class GroupedDesignBaseComponent extends BaseComponent implements OnInit, OnChanges {
  @Input() public selectedPath: ProductionSchedulePath;
  @Input() public isPreSelection = false;
  @Input() public isProductionSchedule = false;
  @Input() public productionSchedule: ProductionSchedule;
  @Input() public presetGridModel = null;
  @Input() public productionScheduleItemMultiplier: number;
  @Input() public selectedRestPathColoredYarnSet: OverviewListColoredYarnSetWithStartDent;
  @Input() public productionSchedulePathsOfColoredYarnSet: ProductionSchedulePathsOfColoredYarnSet;
  @Input() public availableSpaceInBuggyInPicks: number;
  @Input() public buggyLengthMaxInPicks: number;
  @Input() public preselectedDrawings: ListDrawingConfiguration[] = [];
  @Input() public preselectedDesignIds: number[] = [];
  @Input() public preselectedConfigurationIds: number[] = [];
  @Input() public selectedPathContentRowData: ProductionScheduleItemInPathGroup;
  @Output() protected drawingIdSelected = new EventEmitter<number[]>();
  @Output() protected configurationIdSelected = new EventEmitter<number[]>();
  @Output() public selectedViewModeChanged = new EventEmitter<ProductionScheduleDrawingViewMode>();
  @Output() public selectedPathOutput = new EventEmitter<ProductionSchedulePath>();
  @Output() public selectedRestPathColoredYarnSetOutput = new EventEmitter<OverviewListColoredYarnSetWithStartDent>();

  public groupedGridModel: GridModel;
  public groupedDrawings: GroupedDrawing[];
  public detailGridModel: GridModel;
  public statusBar: StatusBar;
  public hasQuantitySuggestionPermission: boolean = false;

  public readonly COMMERCIAL_WIDTH_IN_CM = 'commercialWidthInCM';
  public readonly SLICE_SIZE = 30;
  public readonly DETAIL_ROW_HEADER_HEIGHT = 36;
  public readonly DETAIL_ROW_VERTICAL_PADDING = 32;
  public readonly DETAIL_ROW_SCROLL_BAR = 18;
  public readonly syncWithProductionScheduleSubject = new Subject<void>();

  public readonly COLUMN_UNITS_MAP = new Map<string, {dataFromUnit: Unit; getDisplayUnit: () => Unit}>([
    [this.COMMERCIAL_WIDTH_IN_CM, {dataFromUnit: Unit.MILLIMETER, getDisplayUnit: (): Unit => Unit.CENTIMETER}]
  ]);

  public viewModes: ProductionScheduleDrawingViewMode[] = [
    ProductionScheduleDrawingViewMode.DESIGNS_LIST,
    ProductionScheduleDrawingViewMode.ORDER_LINES,
    ProductionScheduleDrawingViewMode.GROUPED_ORDER_LINES,
    ProductionScheduleDrawingViewMode.GROUPED_DESIGNS
  ];

  public selectedViewMode = ProductionScheduleDrawingViewMode.GROUPED_DESIGNS;
  public showOnlyPreSelectedDrawings = false;

  private lastFocusedGroupedDrawingNodeId: string;
  private readyToFetchRows: boolean = false;

  private readonly WIDTH_IN_PX = 'widthInPx';
  private readonly SHAPE = 'shape';
  private readonly SLICE_SIZE_DETAILS = 30;

  protected groupedGridInstance: AgGridAngular | null = null;

  public constructor(
    @Inject(AUTHENTICATION) protected authentication: Authentication,
    @Inject(DRAWINGS) protected drawings: Drawings,
    @Inject(PRODUCTION_SCHEDULE_PLAN) protected readonly productionSchedulePlan: ProductionSchedulePlan,
    protected readonly gridOptionsBuilderFactoryService: GridOptionsBuilderFactoryService,
    protected readonly colDefBuilderFactory: ColDefBuilderFactoryService,
    protected readonly statusBarService: HttpStatusBarService,
    protected readonly translate: TranslateService,
    protected readonly dialogBuilderFactoryService: DialogBuilderFactoryService,
    protected readonly groupedDrawingsSelectionService: GroupedDrawingsSelectionService,
    protected readonly drawingLibraryHelper: DrawingLibraryHelper,
    protected readonly navigationHelper: NavigationHelperPrototypeService
  ) {
    super();
  }

  public ngOnInit(): void {
    const currentSubscription = this.authentication.getCurrentSubscription();

    this.filterViewModes(currentSubscription);
  }

  protected setGroupedDesignBaseComponentProperties(grid: AgGridAngular): void {
    this.groupedGridInstance = grid;
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (!AssertionUtils.isEmpty(this.preselectedDesignIds) && !this.isPreSelection && 'preselectedDesignIds' in changes) {
      this.showOnlyPreSelectedDrawings = true;
    }

    if (!this.isPreSelection) {
      this.setInitializationFilters();
    }

    this.groupedDrawingsSelectionService.updateDetailCellRendererParams(this.getDetailCellRendererParams());
  }

  public updateGrid(gridModel: GridModel): void {
    this.groupedGridInstance.api.setFilterModel(gridModel.filterModel);
  }

  public getSelectedRows(): number {
    return this.preselectedDesignIds ? this.preselectedDesignIds.length : 0;
  }

  public getCustomSettingsDialogData(): any {
    return {
      showPreselectedItems: this.showOnlyPreSelectedDrawings,
      viewMode: this.selectedViewMode
    } as OrderlineSettingsData;
  }

  public onSelectedViewModeChanged(selectedViewMode: ProductionScheduleDrawingViewMode): void {
    this.selectedViewModeChanged.emit(selectedViewMode);
  }

  public onCustomSettingsChanged(settings: {showPreselectedItems: boolean}): void {
    this.showOnlyPreSelectedDrawings = settings.showPreselectedItems ?? this.showOnlyPreSelectedDrawings;
    this.setInitializationFilters();
  }

  public onCellMouseOver(event: CellMouseOverEvent): void {
    this.lastFocusedGroupedDrawingNodeId = event.node.id;
  }

  public parentNodeSelected(event: RowSelectedEvent): void {
    if (event.node.data.updatingFromDetails) {
      return;
    }

    if (this.lastFocusedGroupedDrawingNodeId !== event.node.id) {
      const selectedDrawings = this.preselectedDrawings.filter((detailDrawingConfig: ListDrawingConfiguration) =>
        AssertionUtils.isNullOrUndefined(detailDrawingConfig.configurationId)
          ? event.node.data.drawingIds.includes(detailDrawingConfig.drawing.id)
          : event.node.data.configurationIds.includes(detailDrawingConfig.configurationId)
      );

      const selectionState = this.groupedDrawingsSelectionService.getParentSelectionState(event.node.data.drawingIds, selectedDrawings);

      (event.node as RowNode).selectThisNode(selectionState);
      return;
    }

    const detailGridId = 'detail_' + event.node.id;
    const detailGrid = event.api.getDetailGridInfo(detailGridId);

    if (detailGrid) {
      detailGrid.api.forEachNode((detailNode: RowNode) => detailNode.setSelected(event.node.isSelected()));
    }

    event.node.data.parentState = event.node.isSelected();

    this.getAndSelectDrawings(event);
  }

  public getDetailCellRendererParams(params?: any): any {
    return {
      parentState: params?.node?.data?.parentState,
      initialIds: params ? params.node.data.childsSelected?.map((drawing: ListDrawingConfiguration) => drawing.id) || [] : undefined,
      extraData: {
        isPreSelection: this.isPreSelection,
        productionScheduleItemMultiplier: this.productionScheduleItemMultiplier,
        selectedPath: this.selectedPath,
        productionSchedule: this.productionSchedule,
        productionSchedulePathsOfColoredYarnSet: this.productionSchedulePathsOfColoredYarnSet,
        selectedRestPathColoredYarnSet: this.selectedRestPathColoredYarnSet,
        hasQuantitySuggestionPermission: this.hasQuantitySuggestionPermission,
        availableSpaceInBuggyInPicks: this.availableSpaceInBuggyInPicks,
        buggyLengthMaxInPicks: this.buggyLengthMaxInPicks
      }
    };
  }

  public getGroupedServerSideDataSource(): IServerSideDatasource {
    if (!this.readyToFetchRows) {
      return {
        getRows: (params: IServerSideGetRowsParams): void => {
          params.success({
            rowData: [],
            rowCount: 0
          });
        }
      };
    } else {
      return {
        getRows: (params: IServerSideGetRowsParams): void => {
          const sortModel: SortModelItem[] = params.request.sortModel;
          const startRow: number = params.request.startRow;
          const endRow: number = params.request.endRow;
          const currentFilterModel: FilterModel | AdvancedFilterModel = params.request.filterModel;
          this.drawingLibraryHelper.savedFilterModel = currentFilterModel;

          this.groupedGridModel = new GridModel(startRow, endRow, sortModel, currentFilterModel);

          const groupedBy = this.getGroupedByColumns(params);

          this.drawings
            .getGroupedDrawings(this.groupedGridModel, groupedBy)
            .pipe(takeUntil(this.unSubscribeOnViewDestroy))
            .subscribe({
              next: (groupedDrawings: GroupedDrawing[]) => {
                this.groupedDrawings = groupedDrawings;

                if (params.request.startRow === 0 && groupedDrawings.length === 0) {
                  this.groupedGridInstance.api.showNoRowsOverlay();
                }

                if (startRow === 0) {
                  this.getStatusBarData(this.groupedGridModel);

                  if (groupedDrawings.length === 0) {
                    params.api.showNoRowsOverlay();
                  } else {
                    params.api.hideOverlay();
                  }
                }

                setTimeout(() => {
                  params.success({
                    rowData: groupedDrawings,
                    rowCount: groupedDrawings.length
                  });

                  this.setSelectedDrawings();
                });
              },
              error: (error: any) => {
                params.fail();
                params.api.showNoRowsOverlay();
                throw error;
              }
            });
        }
      };
    }
  }

  public getGroupColumnDefinitions(): ColDef[] {
    return [
      this.colDefBuilderFactory.getBuilder().withColIdAndField('id').withNumericMultiFilter(this.getPossibleColumnsValues('id')).withHide().withHiddenInToolPanel().build(),
      this.colDefBuilderFactory.getBuilder().withColIdAndField('configurationId').withNumericMultiFilter(this.getPossibleColumnsValues('configurationId')).withHiddenInToolPanel().withHide().build(),
      this.colDefBuilderFactory.getBuilder().withColIdAndField('widthInPxWithoutConfigurations').withNumericMultiFilter().withHiddenInToolPanel().withHide().build(),
      this.colDefBuilderFactory.getBuilder().withColIdAndField('shapeWithoutConfigurations').withTextMultiFilter().withHiddenInToolPanel().withHide().build(),
      this.colDefBuilderFactory
        .getBuilder()
        .withColId('qualityName')
        .withField('quality.name')
        .withValueGetter((params: ValueGetterParams) => this.getDropdownCellValue(params))
        .withTooltipField('quality.name')
        .withCellRenderer('agGroupCellRenderer')
        .withHeaderName('TEXTILE_DATA.MACHINE_QUALITY.MACHINE_QUALITY')
        .withValueGetter((params: ValueGetterParams) => this.getDropdownCellValue(params))
        .withTextMultiFilter(this.getPossibleColumnsValues('qualityName'))
        .withSuppressMovable()
        .build(),
      this.colDefBuilderFactory
        .getBuilder()
        .withColId('colorSetName')
        .withField('colorSet.name')
        .withTooltipField('colorSet.name')
        .withHeaderName('TEXTILE_DATA.COLOR_SET.COLOR_SET')
        .withCellRendererSelector(this.cellRendererSelector())
        .withTextMultiFilter(this.getPossibleColumnsValues('colorSetName'))
        .build(),
      this.colDefBuilderFactory
        .getBuilder()
        .withColId(this.WIDTH_IN_PX)
        .withField(`technicalWidthInPx`)
        .withHeaderName('GENERAL.DIMENSIONS.WIDTH', 1, false, Unit.PIXEL)
        .withNumericMultiFilter(this.getPossibleColumnsValues(this.WIDTH_IN_PX))
        .withRightAlignment()
        .build(),
      this.colDefBuilderFactory
        .getBuilder()
        .withColIdAndField(this.COMMERCIAL_WIDTH_IN_CM)
        .withHeaderName('GENERAL.DIMENSIONS.WIDTH', 1, false, Unit.CENTIMETER)
        .withValueFormatter((params: ValueFormatterParams) => params.data.commercialDimensionsInCM?.toFixed(2))
        .withNumericMultiFilter(this.getPossibleColumnsValues(this.COMMERCIAL_WIDTH_IN_CM), this.getConversionRate(this.COMMERCIAL_WIDTH_IN_CM))
        .withRightAlignment()
        .build(),
      this.colDefBuilderFactory
        .getBuilder()
        .withColIdAndField(this.SHAPE)
        .withHeaderName('GENERAL.SHAPE')
        .withValueGetter((params: ValueGetterParams) => this.getShape(params.data), true)
        .withTextMultiFilter(this.getPossibleColumnsValues(this.SHAPE))
        .withCellClass('cell-vertical-align')
        .build(),
      this.colDefBuilderFactory
        .getBuilder()
        .withColIdAndField('drawingType')
        .withValueGetter((params: ValueGetterParams<any>) => (this.isProductionSchedule ? params.data.drawing.drawingType : params.data))
        .withTextMultiFilter(of(['BMP', 'EP']))
        .withHide()
        .withHiddenInToolPanel()
        .build(),
      this.colDefBuilderFactory
        .getBuilder()
        .withColIdAndField('missingDesign')
        .withValueGetter((params: ValueGetterParams<any>) => (this.isProductionSchedule ? params.data.drawing.missingDesign : params.data))
        .withTextMultiFilter(of(['true', 'false']))
        .withHide()
        .withHiddenInToolPanel()
        .build()
    ];
  }

  public toggleSelectedRows(change: boolean): void {
    if (change) {
      this.drawingLibraryHelper.savedFilterModel = null;
      this.groupedGridInstance?.api?.setFilterModel(null);

      const filterModel = this.drawingLibraryHelper.getPreselectedDrawingAndConfigurationIdsFilterModel(this.preselectedDesignIds, this.preselectedConfigurationIds);

      this.drawingLibraryHelper.savedFilterModel = filterModel;
      this.groupedGridInstance?.api?.setFilterModel(filterModel);
    } else {
      this.setInitializationFilters();
    }
  }

  public setInitializationFilters(): void {
    let filterModel: any;

    filterModel = this.drawingLibraryHelper.getInitializationFilterModel(
      this.showOnlyPreSelectedDrawings,
      this.productionSchedule,
      this.selectedPath,
      this.selectedRestPathColoredYarnSet,
      this.preselectedDesignIds,
      this.preselectedConfigurationIds,
      this.isPreSelection,
      this.selectedPathContentRowData
    );

    this.readyToFetchRows = true;
    this.groupedGridInstance?.api?.setFilterModel(filterModel);

    //because we fetched no rows yet, we need to explictly trigger a new getrows on aggrid
    this.groupedGridInstance?.api?.setGridOption('serverSideDatasource', this.getGroupedServerSideDataSource());
  }

  public getTooltip(key: string): string {
    const translation = this.translate.instant(key);
    return translation.length > 20 ? translation : '';
  }

  private getPossibleColumnsValues(columnIdentifier: string): Observable<string[]> {
    return this.drawings.getPossibleValues(columnIdentifier).pipe(takeUntil(this.unSubscribeOnViewDestroy));
  }

  private getGroupedByColumns(params: IServerSideGetRowsParams): string[] {
    const groupedBy = [];
    if (params.api.getColumn('qualityName').isVisible()) {
      groupedBy.push('QUALITY');
    }
    if (params.api.getColumn('colorSetName').isVisible()) {
      groupedBy.push('COLOR_SET');
    }
    if (params.api.getColumn(this.COMMERCIAL_WIDTH_IN_CM).isVisible()) {
      groupedBy.push('COMMERCIAL_WIDTH_IN_CM');
    }
    if (params.api.getColumn(this.WIDTH_IN_PX).isVisible()) {
      groupedBy.push('TECHNICAL_WIDTH_IN_DENTS');
    }

    if (AssertionUtils.isEmpty(groupedBy)) {
      groupedBy.push('QUALITY');
      groupedBy.push('COLOR_SET');
    }

    return groupedBy;
  }

  private getStatusBarData(gridModel: GridModel): void {
    gridModel.startRow = 0;
    gridModel.endRow = BackendLimitsConstants.INT32_MAX;
    this.statusBarService
      .getStatusBarData(StatusBarTarget.DRAWING_LIBRARY, gridModel)
      .pipe(takeUntil(this.unSubscribeOnViewDestroy))
      .subscribe((statusBar: StatusBar) => {
        statusBar.totalGroupedRows = this.groupedDrawings.length;
        this.statusBar = statusBar;
      });
  }

  private setSelectedDrawings(): void {
    if (this.preselectedDrawings?.length > 0) {
      this.groupedGridInstance?.api?.forEachNode((node: RowNode) => {
        if (!AssertionUtils.isNullOrUndefined(node.data)) {
          const selectedDrawings = this.preselectedDrawings.filter((drawing: ListDrawingConfiguration) =>
            AssertionUtils.isNullOrUndefined(drawing.configurationId) ? node.data.drawingIds.includes(drawing.drawing.id) : node.data.configurationIds.includes(drawing.configurationId)
          );
          const selectionState = this.groupedDrawingsSelectionService.getParentSelectionState(node.data.drawingIds, selectedDrawings);
          node.data.childsSelected = selectedDrawings;
          node.data.parentState = selectionState;
          node.selectThisNode(selectionState);
        }
      });
    }
  }

  private filterViewModes(currentSubscription: Subscription): void {
    if (!currentSubscription.hasPermission(Permission.TEXFAB_ORDERBOOK)) {
      this.viewModes = this.viewModes.filter(
        (viewMode: ProductionScheduleDrawingViewMode) => viewMode !== ProductionScheduleDrawingViewMode.ORDER_LINES && viewMode !== ProductionScheduleDrawingViewMode.GROUPED_ORDER_LINES
      );
    }
  }

  public onDetailRowsSelectionChanged(node: RowNode, parentSelectionState: boolean, drawingsSelected: ListDrawingConfiguration[]): void {
    const deselectedConfigurationIds = node.data.configurationIds.filter((id: number) => !drawingsSelected.some((des: ListDrawingConfiguration) => des.configurationId === id));
    const deselectedDrawingIds = node.data.drawingIds.filter((id: number) => !drawingsSelected.some((des: ListDrawingConfiguration) => des.drawing.id === id));

    let currentSelectedDrawings =
      this.preselectedDrawings?.filter((drawing: ListDrawingConfiguration) =>
        AssertionUtils.isNullOrUndefined(drawing.configurationId) ? !deselectedDrawingIds.includes(drawing.drawing.id) : !deselectedConfigurationIds.includes(drawing.configurationId)
      ) ?? [];

    const drawingsSet = this.getDrawingSet(currentSelectedDrawings, drawingsSelected);

    currentSelectedDrawings = [...drawingsSet];

    this.groupedDrawingsSelectionService.setGroupedDrawingsSelected(currentSelectedDrawings);
    node.data.childsSelected = drawingsSelected;
    node.data.parentState = parentSelectionState;
    node.data.updatingFromDetails = true;
    node.selectThisNode(parentSelectionState);

    setTimeout(() => (node.data.updatingFromDetails = false), 100);

    this.updateSelectedDrawingAndConfigurationIds(currentSelectedDrawings);
  }

  private getAndSelectDrawings(event: RowSelectedEvent): void {
    const drawingIds = event.node.data.drawingIds;
    const configurationIds = event.node.data.configurationIds;
    const sortModel: SortModelItem[] = [];
    let filterModel = {};

    if (!AssertionUtils.isNullOrUndefined(drawingIds) && !AssertionUtils.isEmpty(drawingIds)) {
      filterModel = {
        id: {
          filterModels: [null, {values: drawingIds, filterType: 'set'}],
          filterType: 'multi'
        }
      };

      if (!AssertionUtils.isNullOrUndefined(configurationIds) && !AssertionUtils.isEmpty(configurationIds)) {
        const configurationFilterModel = {
          configurationId: {
            filterModels: [null, {values: configurationIds, filterType: 'set'}],
            filterType: 'multi'
          }
        };

        filterModel = {...filterModel, ...configurationFilterModel};
      }
    }

    this.detailGridModel = new GridModel(0, this.SLICE_SIZE_DETAILS, sortModel, filterModel);

    this.drawings
      .getAllWithoutImageDataFromGridModel(TargetForListOfDrawingsEnum.PRODUCTION_ORDER, this.detailGridModel, true, 'group')
      .pipe(
        map((listOfDrawings: Drawing[]) => {
          return listOfDrawings.map((drawing: Drawing) => {
            const config = this.getDrawingConfiguration(drawing, event.node.data.quality?.name, event.node.data.colorSet?.name, event.node.data.commercialWidthInCM);
            return new ListDrawingConfiguration(drawing, 0, this.productionScheduleItemMultiplier, event.node.data.technicalWidthInPx, config);
          });
        }),
        takeUntil(this.unSubscribeOnViewDestroy)
      )
      .subscribe((drawings: ListDrawingConfiguration[]) => {
        let selectedDrawings: ListDrawingConfiguration[] = [];

        if (event.node.isSelected()) {
          event.node.data.childsSelected = drawings;
        } else if (!AssertionUtils.isNullOrUndefined(event.node.isSelected()) && !event.node.isSelected()) {
          event.node.data.childsSelected = [];
        }

        if (event.node.isSelected()) {
          const drawingsSet = this.getDrawingSet(this.preselectedDrawings, drawings);
          selectedDrawings = [...drawingsSet];
        } else {
          selectedDrawings = this.preselectedDrawings.filter(
            (selectedDrawing: ListDrawingConfiguration) =>
              !drawings.some((drawing: ListDrawingConfiguration) =>
                AssertionUtils.isNullOrUndefined(drawing.configurationId) ? selectedDrawing.drawing.id === drawing.drawing.id : selectedDrawing.configurationId === drawing.configurationId
              )
          );
        }

        this.groupedDrawingsSelectionService.setGroupedDrawingsSelected(selectedDrawings);
        this.updateSelectedDrawingAndConfigurationIds(selectedDrawings);
      });
  }

  private updateSelectedDrawingAndConfigurationIds(selectedDrawings: ListDrawingConfiguration[]): void {
    const selectedDesignIds = selectedDrawings.map((design: ListDrawingConfiguration) => design.drawing.id);
    const selectedConfigurationIds = selectedDrawings.map((design: ListDrawingConfiguration) => design.configurationId).filter((id: number) => id !== undefined);

    this.drawingIdSelected.emit(selectedDesignIds);
    this.configurationIdSelected.emit(selectedConfigurationIds);
  }

  private getDrawingConfiguration(drawing: Drawing, quality: string, colorSetName: string, widthInCM: number): DrawingConfiguration {
    return drawing?.configurations?.find((drawingConfiguration: DrawingConfiguration) => {
      const width = convertToCommercialUnitCentimeter({unit: Unit.MILLIMETER, value: drawingConfiguration.commercialDimensionsInMm.widthInMM} as UnitValue);
      const colorSet = drawingConfiguration instanceof DrawingConfigurationWithColorAndYarnSet ? drawingConfiguration.colorSet : null;
      return drawingConfiguration.quality?.name === quality && colorSet?.name === colorSetName && width === Number(widthInCM);
    });
  }

  private getDrawingSet(currentSelectedDrawings: ListDrawingConfiguration[], drawingsSelected: ListDrawingConfiguration[]): Set<ListDrawingConfiguration> {
    return new Set([
      ...currentSelectedDrawings,
      ...drawingsSelected.filter(
        (newDrawing: ListDrawingConfiguration) =>
          this.preselectedDrawings.findIndex((selectedDrawing: ListDrawingConfiguration) =>
            AssertionUtils.isNullOrUndefined(newDrawing.configurationId) ? selectedDrawing.drawing.name === newDrawing.drawing.name : selectedDrawing.configurationId === newDrawing.configurationId
          ) === -1
      )
    ]);
  }

  private getDropdownCellValue(params: ValueGetterParams): string {
    return `${params.data.quality.name} (${params.data.drawingIds.length})`;
  }

  private cellRendererSelector<TData = any, TValue = any>(cellRenderer: any = null, cellRendererParams: any = null): (params: ICellRendererParams<TData, TValue>) => CellRendererSelectorResult {
    return (params: ICellRendererParams<TData, TValue>): CellRendererSelectorResult =>
      params.node.level === 0 ? {component: cellRenderer ?? params.colDef.cellRenderer, params: cellRendererParams} : {component: EmptyCellRendererComponent};
  }

  private getShape(groupedDrawing: GroupedDrawing): string {
    return FixedShape.getTranslationKeyForShape(groupedDrawing.shape.name, this.translate);
  }

  private getConversionRate(key: string): number {
    return convertUnit({
      from: {
        value: 1,
        unit: this.COLUMN_UNITS_MAP.get(key).dataFromUnit
      },
      to: this.COLUMN_UNITS_MAP.get(key).getDisplayUnit()
    });
  }
}
